import { useState } from "react";
import { FaBuilding, FaDownload, FaRupeeSign } from "react-icons/fa";
import {
  LuBuilding2,
  LuConstruction,
  LuExpand,
  LuShieldCheck,
} from "react-icons/lu";
import { downloadFile, formatPrice } from "../../../utils/helpers";
import PropertyDetailsMediaSection from "../userCommon/PropertyDetailsMediaSection";
import PropertyDetailsHeaderSection from "../userCommon/PropertyDetailsHeaderSection";

const formatStatus = (status) => {
  if (!status) return "N/A";
  const formatted = status.replace(/_/g, " ").toLowerCase();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const DetailsSection = ({ projectData }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const displayPrice = projectData?.property?.price || "N/A";
  const displayBuilder = projectData?.property?.builderName || "Not Specified";
  const displayOwnership = formatStatus(projectData?.property?.ownership);
  const displayStatus = formatStatus(projectData?.property?.availabilityStatus);
  const displayArea = projectData?.property?.floors?.totalArea?.value;
  const displayFloors = projectData?.property?.floors?.totalFloors || "N/A";
  const brochureUrl = projectData?.property?.brochureUrl || "";

  const handleDownloadBrochure = async () => {
    if (!brochureUrl) return;
    setIsDownloading(true);
    try {
      await downloadFile(brochureUrl);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const grayIconClass = "text-gray-500 text-lg sm:text-xl md:text-2xl mt-1";
  const details = [
    {
      id: "ownership",
      label: "Ownership",
      value: displayOwnership,
      icon: <LuShieldCheck className={grayIconClass} />,
    },
    {
      id: "floors",
      label: "Total Floors",
      value: displayFloors,
      icon: <LuBuilding2 className={grayIconClass} />,
    },
    {
      id: "area",
      label: "Total Area",
      value: displayArea,
      icon: <LuExpand className={grayIconClass} />,
    },
    {
      id: "builder",
      label: "Builder:",
      value: displayBuilder,
      icon: <LuConstruction className={grayIconClass} />,
    },
  ];
  return (
    <div className="rounded-xl grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
      {/* Left Section */}
      <div className="xl:col-span-2 border-2 p-3 sm:p-4 rounded-lg">
        <div className="flex items-center gap-3 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-200">
          <FaRupeeSign className="text-green-600 text-xl sm:text-2xl" />
          <div>
            <p className="font-semibold text-[#031722] tracking-[0.01em] text-lg sm:text-xl md:text-2xl xl:text-[24px]">
              {formatPrice(displayPrice)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 py-4 sm:py-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FaBuilding className={`${grayIconClass} mt-1`} />
            <div>
              <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
                Project Status
              </p>
              <p className="font-medium text-[#383838] text-sm sm:text-base md:text-lg xl:text-[18px]">
                {displayStatus}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 sm:pt-6 md:divide-x divide-gray-300">
          {details.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center md:items-start gap-3 ${
                index === 0 ? "sm:pr-4" : "sm:px-4"
              } w-full sm:w-auto`}
            >
              {/* ICON */}
              {item.icon && item.icon}

              {/* TEXT */}
              <div>
                <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
                  {item.label}
                </p>
                <p className="font-medium text-[#383838] tracking-[0.01em] text-sm sm:text-base md:text-lg xl:text-[18px]">
                  {item.value}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Section */}
      <div className="xl:col-span-1 border-2 p-3 sm:p-4 rounded-lg">
        <div>
          <h3 className="font-semibold text-[#031722] tracking-[0.01em] leading-[150%] text-sm sm:text-base xl:text-[16px] mb-3 sm:mb-4">
            Why you should consider Covenant Developers?
          </h3>

          <ul className="space-y-3 text-xs sm:text-sm text-gray-700 mb-4 sm:mb-6">
            <li className="flex gap-2">
              <span className="font-normal text-[#858585]">1</span>
              <span>
                Strategically located near Sardar Patel Ring Road and 5 G Road.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-normal text-[#858585]">2</span>
              <span>Spacious 3BHK apartments, 1117–1419 sq ft.</span>
            </li>
            <li className="flex gap-2">
              <span className="font-normal text-[#858585]">3</span>
              <span>
                Power backup facilities{" "}
                <span className="font-medium text-[#858585] underline">
                  Read More
                </span>
              </span>
            </li>
          </ul>

          <button
            onClick={handleDownloadBrochure}
            disabled={!brochureUrl || isDownloading}
            className={`w-full font-bold py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm sm:text-base ${
              brochureUrl && !isDownloading
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            <FaDownload />
            {isDownloading ? "Downloading..." : "Download Brochure"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProjectDetailsCard = ({ projectData }) => {
  const [activeTab, setActiveTab] = useState("about");

  const { title, images = [] } = projectData || {};
  const displayTitle = title || "Project Title Not Available";
  const companyId = projectData?.property?.companyId;

  return (
    <div className="py-4 sm:py-6 md:py-8 xl:py-10">
      <div className="mx-auto bg-white px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-4 sm:py-6">
        <PropertyDetailsHeaderSection
          displayTitle={displayTitle}
          location={projectData?.location}
          companyId={companyId}
        />

        <PropertyDetailsMediaSection images={images} />

        <DetailsSection projectData={projectData} />
      </div>

      {/* Bottom Navigation Tabs - unchanged */}
      <div className="bg-white sticky bottom-0 border-t">
        <div className="mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
          <div className="flex justify-between overflow-x-auto">
            {[
              { id: "about", label: "About" },
              { id: "floor", label: "Floor Plan" },
              { id: "amenities", label: "Amenities" },
              { id: "boucher", label: "Brochure" },
              { id: "map", label: "Map & Landmark" },
              { id: "specs", label: "Specifications" },
              { id: "payment", label: "Payment Schedule" },
              { id: "facilities", label: "Facilities" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`font-medium text-[#031722] tracking-[0.01em] leading-[150%] text-center text-xs sm:text-sm md:text-base xl:text-[20px] border-b-2 py-2 px-1 sm:px-2 whitespace-nowrap min-w-0 flex-shrink-0 ${
                  activeTab === tab.id
                    ? "border-gray-900 text-gray-900"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsCard;

// import React, { useState } from "react";
// import { FaRupeeSign, FaBuilding, FaExpand, FaDownload } from "react-icons/fa";
// import { downloadFile, formatPrice } from "../../../utils/helpers";
// import { SeeOnMap } from "../../admin/common/details-components";

// const ProjectDetailsCard = ({ projectData }) => {
//   const [activeTab, setActiveTab] = useState("about");
//   const [isDownloading, setIsDownloading] = useState(false);
//   const [selectedImage, setSelectedImage] = useState(null);

//   const tabs = [
//     { id: "about", label: "About" },
//     { id: "floor", label: "Floor Plan" },
//     { id: "amenities", label: "Amenities" },
//     { id: "boucher", label: "Brochure" },
//     { id: "map", label: "Map & Landmark" },
//     { id: "specs", label: "Specifications" },
//     { id: "payment", label: "Payment Schedule" },
//     { id: "facilities", label: "Facilities" },
//   ];

//   const { title, images = [] } = projectData || {};

//   const formatStatus = (status) => {
//     if (!status) return "N/A";

//     // Replace underscores with spaces
//     const formatted = status.replace(/_/g, " ").toLowerCase();

//     // Capitalize only the FIRST letter of the FIRST word
//     return formatted.charAt(0).toUpperCase() + formatted.slice(1);
//   };

//   const displayTitle = title || "Project Title Not Available";
//   const displayPrice = projectData?.property?.price || "N/A";
//   const displayBuilder = projectData?.property?.builderName || "Not Specified";
//   const displayOwnership =
//     formatStatus(projectData?.property?.ownership) || "N/A";
//   const displayStatus = formatStatus(projectData?.property?.availabilityStatus);
//   const displayArea = projectData?.property?.floors?.totalArea?.value;
//   const displayFloors = projectData?.property?.floors?.totalFloors || "N/A";
//   const brochureUrl = projectData?.property?.brochureUrl || "";
//   const companyId = projectData?.property?.companyId;
//   // console.log("companyid:", companyId);
//   const mainImage =
//     selectedImage ||
//     images.find((img) => img.isThumbnail)?.url ||
//     images[0]?.url;

//   const handleDownloadBrochure = async () => {
//     if (!brochureUrl) return;

//     setIsDownloading(true);
//     try {
//       await downloadFile(brochureUrl);
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setIsDownloading(false);
//     }
//   };

//   return (
//     <div className="py-4 sm:py-6 md:py-8 xl:py-10">
//       <div className="mx-auto bg-white px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 py-4 sm:py-6">
//         {/* Header */}
//         <div className="mb-2 sm:mb-3 md:mb-4">
//           {/* title */}
//           <h2 className="font-medium text-[#031722] tracking-[0.01em] leading-[150%] text-lg sm:text-2xl md:text-3xl xl:text-[24px] mb-2 sm:mb-3 md:mb-4">
//             {displayTitle}
//           </h2>

//           <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-1">
//             <SeeOnMap location={projectData?.location} />
//             <div className="w-full lg:w-auto flex justify-between">
//               {companyId && (
//                 <button
//                   onClick={() => {
//                     window.location.href = `/company-details/${companyId}`;
//                     window.scrollTo({ top: 0, behavior: "smooth" });
//                   }}
//                   className="text-red-600 hover:text-red-700 transition-colors underline text-sm sm:text-base md:text-lg xl:text-base font-medium flex items-center tracking-wide"
//                 >
//                   View Developer's Details
//                 </button>
//               )}
//             </div>
//           </div>
//         </div>

//         {/* Main Section */}
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
//           <div className="lg:col-span-2">
//             <div className="bg-white rounded-xl shadow-md overflow-hidden">
//               <img
//                 src={mainImage}
//                 alt="Entrance Gate view"
//                 className="w-full h-44 md:h-72 xl:h-96 object-cover transition-all duration-300"
//               />
//             </div>
//           </div>

//           <div className="lg:col-span-1">
//             <div className="flex lg:grid lg:grid-cols-2 gap-3 sm:gap-4 overflow-x-auto pb-2">
//               {images.slice(1, 5).map((img, index) => (
//                 <div
//                   key={index}
//                   onClick={() => setSelectedImage(img.url)}
//                   className={`bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer flex-shrink-0`}
//                 >
//                   <img
//                     src={img.url}
//                     alt={`Floor plan ${index + 1}`}
//                     className="w-32 sm:w-36 md:w-40 xl:w-full h-28 sm:h-32 md:h-36 xl:h-44 object-cover hover:scale-105 transition-transform"
//                   />
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>

//         {/* Price and Details Section (unchanged) */}
//         <div className="rounded-xl grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
//           <div className="xl:col-span-2 border-2 p-3 sm:p-4 rounded-lg">
//             <div className="flex items-center gap-3 sm:gap-4 pb-4 sm:pb-6 border-b border-gray-200">
//               <FaRupeeSign className="text-green-600 text-xl sm:text-2xl" />
//               <div>
//                 <p className="font-semibold text-[#031722] tracking-[0.01em] text-lg sm:text-xl md:text-2xl xl:text-[24px]">
//                   {formatPrice(displayPrice)}
//                 </p>
//               </div>
//             </div>

//             <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 py-4 sm:py-6 border-b border-gray-200">
//               <div className="flex gap-3">
//                 <FaBuilding className="text-blue-600 text-lg sm:text-xl mt-1" />
//                 <div>
//                   <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
//                     Project Status
//                   </p>
//                   <p className="font-medium text-[#383838] text-sm sm:text-base md:text-lg xl:text-[18px]">
//                     {displayStatus}
//                   </p>
//                 </div>
//               </div>
//             </div>

//             <div className="flex flex-col sm:flex-row items-start gap-4 pt-4 sm:pt-6 sm:divide-x divide-gray-300">
//               <div className="flex items-start gap-3 sm:pr-4 w-full sm:w-auto">
//                 <FaBuilding className="text-blue-600 text-lg sm:text-xl mt-1" />
//                 <div>
//                   <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
//                     Ownership
//                   </p>
//                   <p className="font-medium text-[#383838] tracking-[0.01em] text-sm sm:text-base md:text-lg xl:text-[18px]">
//                     {displayOwnership}
//                   </p>
//                 </div>
//               </div>

//               <div className="flex items-start gap-3 sm:px-4 w-full sm:w-auto">
//                 <FaBuilding className="text-blue-600 text-lg sm:text-xl mt-1" />
//                 <div>
//                   <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
//                     Total Floors
//                   </p>
//                   <p className="font-medium text-[#383838] tracking-[0.01em] text-sm sm:text-base md:text-lg xl:text-[18px]">
//                     {displayFloors}
//                   </p>
//                 </div>
//               </div>

//               <div className="flex items-start gap-3 sm:px-4 w-full sm:w-auto">
//                 <FaExpand className="text-blue-600 text-lg sm:text-xl mt-1" />
//                 <div>
//                   <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
//                     Total Area
//                   </p>
//                   <p className="font-medium text-[#383838] tracking-[0.01em] text-sm sm:text-base md:text-lg xl:text-[18px]">
//                     {displayArea}
//                   </p>
//                 </div>
//               </div>

//               <div className="flex items-start gap-3 sm:px-4 w-full sm:w-auto">
//                 <div>
//                   <p className="font-normal text-[#858585] text-xs sm:text-sm xl:text-[14px]">
//                     Builder:
//                   </p>
//                   <p className="font-medium text-[#383838] tracking-[0.01em] text-sm sm:text-base md:text-lg xl:text-[18px]">
//                     {displayBuilder}
//                   </p>
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="xl:col-span-1 border-2 p-3 sm:p-4 rounded-lg">
//             <div>
//               <h3 className="font-semibold text-[#031722] tracking-[0.01em] leading-[150%] text-sm sm:text-base xl:text-[16px] mb-3 sm:mb-4">
//                 Why you should consider Covenant Developers?
//               </h3>
//               <ul className="space-y-3 text-xs sm:text-sm text-gray-700 mb-4 sm:mb-6">
//                 <li className="flex gap-2">
//                   <span className="font-normal text-[#858585]">1</span>
//                   <span>
//                     Strategically located near Sardar Patel Ring Road and 5 G
//                     Road.
//                   </span>
//                 </li>
//                 <li className="flex gap-2">
//                   <span className="font-normal text-[#858585]">2</span>
//                   <span>Spacious 3BHK apartments, 1117–1419 sq ft.</span>
//                 </li>
//                 <li className="flex gap-2">
//                   <span className="font-normal text-[#858585]">3</span>
//                   <span>
//                     Power backup facilities{" "}
//                     <span className="font-medium text-[#858585] underline">
//                       Read More
//                     </span>
//                   </span>
//                 </li>
//               </ul>
//               <button
//                 onClick={handleDownloadBrochure}
//                 disabled={!brochureUrl || isDownloading}
//                 className={`w-full font-bold py-2 sm:py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm sm:text-base ${
//                   brochureUrl && !isDownloading
//                     ? "bg-red-600 hover:bg-red-700 text-white"
//                     : "bg-gray-300 text-gray-500 cursor-not-allowed"
//                 }`}
//               >
//                 <FaDownload />
//                 {isDownloading ? "Downloading..." : "Download Brochure"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Bottom Navigation Tabs */}
//       <div className="bg-white sticky bottom-0 border-t">
//         <div className="mx-auto px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10">
//           <div className="flex justify-between overflow-x-auto">
//             {tabs.map((tab) => (
//               <button
//                 key={tab.id}
//                 onClick={() => setActiveTab(tab.id)}
//                 className={`font-medium text-[#031722] tracking-[0.01em] leading-[150%] text-center text-xs sm:text-sm md:text-base xl:text-[20px] border-b-2 py-2 px-1 sm:px-2 whitespace-nowrap min-w-0 flex-shrink-0 ${
//                   activeTab === tab.id
//                     ? "border-gray-900 text-gray-900"
//                     : "border-transparent text-gray-600 hover:text-gray-900"
//                 }`}
//               >
//                 {tab.label}
//               </button>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProjectDetailsCard;
